import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import contentAPI from '../../services/contentAPI';
import {
  TrendingTopic,
  TrendingTopicsRequest,
  GeneratePostRequest,
  GeneratedPost,
  GeneratedPostVariant,
  GeneratePostResponse,
  GenerationJob,
  ImageSearchRequest,
  ImageSearchSuggestionsRequest,
  ImageSearchBatchRequest,
  ImageSelectRequest,
  ImageCandidate,
  PlatformDraft,
  RegeneratePartRequest,
} from '../../types/api';

interface ContentState {
  trendingTopics: TrendingTopic[];
  generatedContent: GeneratedPost | null;
  postVariants: GeneratedPostVariant[];
  selectedVariantIndex: number;
  platformDrafts: Record<string, PlatformDraft>;
  activePlatform: string;
  selectedImage: {
    url: string;
    path: string;
    credit: string;
  } | null;
  imageSuggestions: string[];
  imageQuery: string;
  imageCandidates: ImageCandidate[];
  imageSuggestionsLoading: boolean;
  imageSearchLoading: boolean;
  imageSelecting: boolean;
  loading: boolean;
  queueingPost: boolean;
  generationJob: GenerationJob | null;
  partRegenerating: boolean;
  shorteningForX: boolean;
  error: string | null;
}

const initialState: ContentState = {
  trendingTopics: [],
  generatedContent: null,
  postVariants: [],
  selectedVariantIndex: 0,
  platformDrafts: {},
  activePlatform: 'linkedin',
  selectedImage: null,
  imageSuggestions: [],
  imageQuery: '',
  imageCandidates: [],
  imageSuggestionsLoading: false,
  imageSearchLoading: false,
  imageSelecting: false,
  loading: false,
  queueingPost: false,
  generationJob: null,
  partRegenerating: false,
  shorteningForX: false,
  error: null,
};

function applyGenerateResponse(
  state: ContentState,
  payload: GeneratePostResponse
) {
  const post = payload.data;
  state.generatedContent = post;
  state.postVariants = payload.variants ?? [];
  state.selectedVariantIndex = 0;
  if (post.content_by_platform) {
    state.platformDrafts = post.content_by_platform;
    const keys = Object.keys(post.content_by_platform);
    state.activePlatform = keys.includes('linkedin') ? 'linkedin' : keys[0];
  } else {
    state.platformDrafts = {
      linkedin: {
        platform: 'linkedin',
        content: post.content,
        char_count: post.content.length,
        char_limit: 3000,
        within_limit: post.content.length <= 3000,
      },
    };
    state.activePlatform = 'linkedin';
  }
  if (post.image) {
    state.selectedImage = {
      url: post.image.url,
      path: post.image.path,
      credit: post.image.credit,
    };
  }
  state.error = null;
}

// Async thunks
export const fetchTrendingTopics = createAsyncThunk(
  'content/fetchTrendingTopics',
  async (params: TrendingTopicsRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.getTrendingTopics(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch trending topics');
    }
  }
);

export const startGeneratePost = createAsyncThunk(
  'content/startGeneratePost',
  async (request: GeneratePostRequest, { rejectWithValue }) => {
    try {
      const started = await contentAPI.startGeneratePostAsync(request);
      return {
        jobId: started.job_id,
        taskId: started.task_id,
        topic: request.topic,
        status: started.status,
        message: started.message,
        variants: request.variants ?? 1,
      } satisfies GenerationJob;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.message ||
          'Could not queue generation. Is Celery running? (run_celery.cmd)'
      );
    }
  }
);

/** @deprecated Use startGeneratePost + background polling */
export const generatePostContent = startGeneratePost;

export const generateAndSavePost = createAsyncThunk(
  'content/generateAndSavePost',
  async (request: GeneratePostRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.generateAndSavePost(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to generate and save post');
    }
  }
);

export const regeneratePostPart = createAsyncThunk(
  'content/regeneratePart',
  async (request: RegeneratePartRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.regeneratePart(request);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to regenerate content');
    }
  }
);

export const shortenForX = createAsyncThunk(
  'content/shortenForX',
  async (
    params: { topic: string; source_content: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await contentAPI.shortenForX(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to shorten for X');
    }
  }
);

export const searchImage = createAsyncThunk(
  'content/searchImage',
  async (params: ImageSearchRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.searchImage(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to search image');
    }
  }
);

export const getImageSuggestions = createAsyncThunk(
  'content/getImageSuggestions',
  async (params: ImageSearchSuggestionsRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.getImageSearchSuggestions(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to get image suggestions');
    }
  }
);

export const searchImagesBatch = createAsyncThunk(
  'content/searchImagesBatch',
  async (params: ImageSearchBatchRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.searchImagesBatch(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to search images');
    }
  }
);

export const selectImageFromSearch = createAsyncThunk(
  'content/selectImageFromSearch',
  async (params: ImageSelectRequest, { rejectWithValue }) => {
    try {
      const response = await contentAPI.selectImage(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save selected image');
    }
  }
);

export const generateHashtags = createAsyncThunk(
  'content/generateHashtags',
  async (content: string, { rejectWithValue }) => {
    try {
      const response = await contentAPI.generateHashtags(content);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to generate hashtags');
    }
  }
);

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateGenerationJob: (state, action: PayloadAction<Partial<GenerationJob>>) => {
      if (state.generationJob) {
        state.generationJob = { ...state.generationJob, ...action.payload };
      }
    },
    applyGenerateResult: (state, action: PayloadAction<GeneratePostResponse>) => {
      applyGenerateResponse(state, action.payload);
      if (state.generationJob) {
        state.generationJob = {
          ...state.generationJob,
          status: 'SUCCESS',
          message: 'Post ready — review your drafts below.',
        };
      }
    },
    clearGenerationJob: (state) => {
      state.generationJob = null;
    },
    failGenerationJob: (state, action: PayloadAction<string>) => {
      if (state.generationJob) {
        state.generationJob = {
          ...state.generationJob,
          status: 'FAILURE',
          message: action.payload,
        };
      }
      state.error = action.payload;
    },
    setSelectedImage: (state, action: PayloadAction<{ url: string; path: string; credit: string } | null>) => {
      state.selectedImage = action.payload;
    },
    clearGeneratedContent: (state) => {
      state.generatedContent = null;
      state.postVariants = [];
      state.selectedVariantIndex = 0;
      state.platformDrafts = {};
      state.activePlatform = 'linkedin';
      state.imageSuggestions = [];
      state.imageQuery = '';
      state.imageCandidates = [];
      state.selectedImage = null;
    },
    setActivePlatform: (state, action: PayloadAction<string>) => {
      state.activePlatform = action.payload;
    },
    updatePlatformDraftContent: (
      state,
      action: PayloadAction<{ platform: string; content: string }>
    ) => {
      const { platform, content } = action.payload;
      const draft = state.platformDrafts[platform];
      if (draft) {
        draft.content = content;
        draft.char_count = content.length;
        draft.within_limit = content.length <= draft.char_limit;
      }
      if (platform === 'linkedin' && state.generatedContent) {
        state.generatedContent.content = content;
      }
    },
    updateGeneratedContent: (state, action: PayloadAction<Partial<GeneratedPost>>) => {
      if (state.generatedContent) {
        state.generatedContent = { ...state.generatedContent, ...action.payload };
      }
    },
    setImageSuggestions: (state, action: PayloadAction<string[]>) => {
      state.imageSuggestions = action.payload;
    },
    setImageQuery: (state, action: PayloadAction<string>) => {
      state.imageQuery = action.payload;
    },
    clearImageCandidates: (state) => {
      state.imageCandidates = [];
    },
    setGeneratedContent: (state, action: PayloadAction<GeneratedPost>) => {
      state.generatedContent = action.payload;
      if (action.payload.content_by_platform) {
        state.platformDrafts = action.payload.content_by_platform;
        const keys = Object.keys(action.payload.content_by_platform);
        state.activePlatform = keys.includes('linkedin') ? 'linkedin' : keys[0];
      } else {
        state.platformDrafts = {
          linkedin: {
            platform: 'linkedin',
            content: action.payload.content,
            char_count: action.payload.content.length,
            char_limit: 3000,
            within_limit: action.payload.content.length <= 3000,
          },
        };
        state.activePlatform = 'linkedin';
      }
    },
    applyPostVariant: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      const variant = state.postVariants[index];
      if (!variant) return;

      state.selectedVariantIndex = index;
      state.generatedContent = {
        content: variant.content,
        image_search_terms: variant.image_search_terms,
        image: state.generatedContent?.image,
        content_by_platform: variant.content_by_platform,
      };
      state.platformDrafts = variant.content_by_platform;
      state.imageQuery = variant.image_search_terms;
      const keys = Object.keys(variant.content_by_platform);
      state.activePlatform = keys.includes('linkedin') ? 'linkedin' : keys[0];
    },
  },
  extraReducers: (builder) => {
    builder
      // Trending Topics
      .addCase(fetchTrendingTopics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrendingTopics.fulfilled, (state, action) => {
        state.loading = false;
        state.trendingTopics = action.payload;
        state.error = null;
      })
      .addCase(fetchTrendingTopics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Start async generate (queue only — polling in UI)
      .addCase(startGeneratePost.pending, (state) => {
        state.queueingPost = true;
        state.error = null;
      })
      .addCase(startGeneratePost.fulfilled, (state, action) => {
        state.queueingPost = false;
        state.generationJob = action.payload;
      })
      .addCase(startGeneratePost.rejected, (state, action) => {
        state.queueingPost = false;
        state.error = action.payload as string;
      })
      .addCase(regeneratePostPart.pending, (state) => {
        state.partRegenerating = true;
        state.error = null;
      })
      .addCase(regeneratePostPart.fulfilled, (state, action) => {
        state.partRegenerating = false;
        const draft = action.payload;
        state.platformDrafts[draft.platform] = draft;
        if (draft.platform === 'linkedin' && state.generatedContent) {
          state.generatedContent.content = draft.content;
        }
        state.error = null;
      })
      .addCase(regeneratePostPart.rejected, (state, action) => {
        state.partRegenerating = false;
        state.error = action.payload as string;
      })
      .addCase(shortenForX.pending, (state) => {
        state.shorteningForX = true;
        state.error = null;
      })
      .addCase(shortenForX.fulfilled, (state, action) => {
        state.shorteningForX = false;
        const draft = action.payload;
        state.platformDrafts[draft.platform] = draft;
        state.activePlatform = draft.platform;
        state.error = null;
      })
      .addCase(shortenForX.rejected, (state, action) => {
        state.shorteningForX = false;
        state.error = action.payload as string;
      })
      // Generate and Save Post
      .addCase(generateAndSavePost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateAndSavePost.fulfilled, (state, action) => {
        state.loading = false;
        state.generatedContent = action.payload.data;
        state.error = null;
      })
      .addCase(generateAndSavePost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Search Image
      .addCase(searchImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchImage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.selectedImage = action.payload;
        }
        state.error = null;
      })
      .addCase(searchImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getImageSuggestions.pending, (state) => {
        state.imageSuggestionsLoading = true;
        state.error = null;
      })
      .addCase(getImageSuggestions.fulfilled, (state, action) => {
        state.imageSuggestionsLoading = false;
        state.imageSuggestions = action.payload;
        if (!state.imageQuery && action.payload.length > 0) {
          state.imageQuery = action.payload[0];
        }
      })
      .addCase(getImageSuggestions.rejected, (state, action) => {
        state.imageSuggestionsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(searchImagesBatch.pending, (state) => {
        state.imageSearchLoading = true;
        state.error = null;
      })
      .addCase(searchImagesBatch.fulfilled, (state, action) => {
        state.imageSearchLoading = false;
        state.imageCandidates = action.payload.data;
        state.error = null;
      })
      .addCase(searchImagesBatch.rejected, (state, action) => {
        state.imageSearchLoading = false;
        state.error = action.payload as string;
      })
      .addCase(selectImageFromSearch.pending, (state) => {
        state.imageSelecting = true;
        state.error = null;
      })
      .addCase(selectImageFromSearch.fulfilled, (state, action) => {
        state.imageSelecting = false;
        if (action.payload) {
          state.selectedImage = action.payload;
        }
        state.error = null;
      })
      .addCase(selectImageFromSearch.rejected, (state, action) => {
        state.imageSelecting = false;
        state.error = action.payload as string;
      })
      // Generate Hashtags
      .addCase(generateHashtags.fulfilled, (state, action) => {
        const platform = state.activePlatform || 'linkedin';
        const draft = state.platformDrafts[platform];
        if (draft) {
          const updated = `${draft.content} ${action.payload.join(' ')}`.trim();
          draft.content = updated;
          draft.char_count = updated.length;
          draft.within_limit = updated.length <= draft.char_limit;
        }
        if (state.generatedContent && platform === 'linkedin') {
          state.generatedContent.content = state.platformDrafts.linkedin?.content ?? state.generatedContent.content;
        }
      });
  },
});

export const {
  clearError,
  updateGenerationJob,
  applyGenerateResult,
  clearGenerationJob,
  failGenerationJob,
  setSelectedImage,
  clearGeneratedContent,
  updateGeneratedContent,
  setImageSuggestions,
  setImageQuery,
  clearImageCandidates,
  setGeneratedContent,
  applyPostVariant,
  setActivePlatform,
  updatePlatformDraftContent,
} = contentSlice.actions;

export default contentSlice.reducer; 